import ControlPanelController from "./control_panel_controller"

export default class extends ControlPanelController {
  static targets = [
    "searchInput", "tableBody", "sentinel",
    "createModal", "editModal", "confirmModal", "confirmName",
    "createCode", "createName", "createAddress", "createCity", "createState",
    "createZipcode", "createCountry", "createRegion", "createAffiliate", "createBuildingType",
    "createCodeError", "createNameError", "createAddressError", "createCityError", "createCountryError", "createStateError",
    "createStateMark",
    "editId", "editCode", "editName", "editAddress", "editCity", "editState",
    "editZipcode", "editCountry",
    "editRegion", "editAffiliate", "editBuildingType",
    "rowMenu",
    "exportButton",
    "importModal", "importFile", "importButton",
    "allowCustomProjectName"
  ]

  static values = {
    listUrl: String,
    createUrl: String,
    baseUrl: String,
    exportUrl: String,
    importUrl: String,
    toggleCustomNameUrl: String,
    allowCustomProjectName: Boolean
  }

  connect() {
    super.connect()
    this.currentPage = 1
    this.currentSort = ""
    this.currentDesc = false
    this.done = false
    this.loading = false
    this.abortController = null

    // Force the toggle to reflect the server-side truth — defends against
    // browser bfcache/autocomplete restoring a stale checked state.
    if (this.hasAllowCustomProjectNameTarget) {
      this.allowCustomProjectNameTarget.checked = this.allowCustomProjectNameValue
    }

    this.loadPage(1)

    this._onScroll = this._throttle(() => this._checkAndLoad(), 200)
    window.addEventListener("scroll", this._onScroll, { passive: true })

    this._onScrollEnd = () => this._checkAndLoad()
    window.addEventListener("scrollend", this._onScrollEnd, { passive: true })
  }

  disconnect() {
    window.removeEventListener("scroll", this._onScroll)
    window.removeEventListener("scrollend", this._onScrollEnd)
    this.abortController?.abort()
    clearTimeout(this._recheckTimer)
  }

  // ── Search ────────────────────────────────────────────────────────

  search() {
    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => {
      this._resetAndLoad()
    }, 400)
  }

  // ── Sort ──────────────────────────────────────────────────────────

  sort(event) {
    const col = event.currentTarget.dataset.sort
    if (this.currentSort === col) {
      this.currentDesc = !this.currentDesc
    } else {
      this.currentSort = col
      this.currentDesc = false
    }
    this._resetAndLoad()
  }

  // ── Infinite scroll ───────────────────────────────────────────────

  // AI Smart Import just added properties - show them without a page reload.
  reload() {
    this._resetAndLoad()
  }

  _resetAndLoad() {
    this.abortController?.abort()
    this.abortController = null
    this.loading = false
    this.currentPage = 1
    this.done = false
    this.loadPage(1)
  }

  _checkAndLoad(force = false) {
    if (this.loading || this.done) return
    if (!force && !this._isNearBottom()) return
    this._loadNextPage()
  }

  _loadNextPage() {
    this.loadPage(this.currentPage + 1, true)
  }

  _isNearBottom() {
    const scrollBottom = window.innerHeight + window.scrollY
    const docHeight = document.documentElement.scrollHeight
    return docHeight - scrollBottom < 300
  }

  _showSentinel() {
    if (this.hasSentinelTarget) this.sentinelTarget.classList.remove("hidden")
  }

  _hideSentinel() {
    if (this.hasSentinelTarget) this.sentinelTarget.classList.add("hidden")
  }

  // ── Data loading ──────────────────────────────────────────────────

  async loadPage(page, append = false) {
    if (append && this.done) return

    this.loading = true
    if (append) this._showSentinel()

    this.abortController?.abort()
    this.abortController = new AbortController()
    const { signal } = this.abortController

    const wasNearBottom = this._isNearBottom()

    const url = new URL(this.listUrlValue, window.location.origin)
    url.searchParams.set("page", page)
    if (this.hasSearchInputTarget && this.searchInputTarget.value.trim()) {
      url.searchParams.set("search", this.searchInputTarget.value.trim())
    }
    if (this.currentSort) {
      url.searchParams.set("sort", this.currentSort)
      url.searchParams.set("desc", this.currentDesc)
    }

    try {
      const response = await fetch(url, { headers: { Accept: "application/json" }, signal })
      const data = await response.json()
      if (signal.aborted) return

      this.currentPage = data.current_page

      if (append) {
        this._appendRows(data.tag_names)
      } else {
        this._renderTable(data.tag_names)
      }

      if (data.is_last_page) {
        this.done = true
      }
    } catch (e) {
      if (e.name === "AbortError") return
      if (!append) {
        this.tableBodyTarget.innerHTML = '<tr><td colspan="8" class="px-3 py-8 text-center text-red-400 text-[13px]">Error loading data</td></tr>'
      }
    } finally {
      if (!signal.aborted) {
        this.loading = false
        this.abortController = null
        this._hideSentinel()

        if (wasNearBottom && !this.done) {
          this._checkAndLoad(true)
        }
        clearTimeout(this._recheckTimer)
        this._recheckTimer = setTimeout(() => this._checkAndLoad(), 300)
      }
    }
  }

  _renderTable(tagNames) {
    if (!tagNames || tagNames.length === 0) {
      this.tableBodyTarget.innerHTML = '<tr><td colspan="8" class="px-3 py-8 text-center text-gray-400 text-[13px]">No locations found</td></tr>'
      return
    }
    this.tableBodyTarget.innerHTML = tagNames.map((tn, i) => this._buildRow(tn, i)).join("")
  }

  _appendRows(tagNames) {
    if (!tagNames || tagNames.length === 0) return
    const existingCount = this.tableBodyTarget.querySelectorAll("tr").length
    const html = tagNames.map((tn, i) => this._buildRow(tn, existingCount + i)).join("")
    this.tableBodyTarget.insertAdjacentHTML("beforeend", html)
  }

  _buildRow(tn, index) {
    return `
      <tr class="${index % 2 ? 'bg-gray-50/60' : 'bg-white'} border-b border-gray-100 hover:bg-brand/[0.04]">
        <td class="px-3 py-1.5 text-[13px] text-gray-500 truncate">${this._esc(tn.code)}</td>
        <td class="px-3 py-1.5 text-[13px] text-gray-900 font-medium truncate">${this._esc(tn.name)}</td>
        <td class="px-3 py-1.5 text-[13px] text-gray-600 truncate">${this._esc(tn.city || '')}</td>
        <td class="px-3 py-1.5 text-[13px] text-gray-600 truncate">${this._esc(tn.state || '')}</td>
        <td class="px-3 py-1.5 text-[13px] text-gray-600 truncate">${this._esc(tn.region || '')}</td>
        <td class="px-3 py-1.5 text-[13px] text-gray-600 truncate">${this._esc(tn.affiliate_name || '')}</td>
        <td class="px-3 py-1.5 text-[13px] text-gray-600 truncate">${this._esc(tn.building_type_name || '')}</td>
        <td class="px-3 py-1.5 text-right relative whitespace-nowrap">
          <button type="button" class="p-1 rounded hover:bg-gray-100 cursor-pointer"
                  data-action="click->cpanel-locations#toggleRowMenu"
                  data-tag='${JSON.stringify(tn).replace(/'/g, "&#39;")}'>
            <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"/></svg>
          </button>
          <div class="hidden absolute right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-32 whitespace-normal"
               data-cpanel-locations-target="rowMenu">
            <button type="button" class="block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                    data-action="click->cpanel-locations#editFromMenu">Edit</button>
            <button type="button" class="block w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 cursor-pointer"
                    data-action="click->cpanel-locations#deleteFromMenu">Delete</button>
          </div>
        </td>
      </tr>`
  }

  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str || ""
    return d.innerHTML
  }

  // ── Throttle helper ───────────────────────────────────────────────

  _throttle(fn, ms) {
    let last = 0
    let trailing = null
    return () => {
      const now = Date.now()
      clearTimeout(trailing)
      if (now - last >= ms) {
        last = now
        fn()
      } else {
        trailing = setTimeout(() => {
          last = Date.now()
          fn()
        }, ms - (now - last))
      }
    }
  }

  // ── Country / State ──────────────────────────────────────────────

  createCountryChanged() {
    this._markCreateState()
    this._loadSubregions(this.createCountryTarget.value, this.createStateTarget)
  }

  // A state is part of a US address only: a property in the UK, Portugal or
  // Cape Verde has none to pick (Luma's sites, 2026-10-06).
  _stateNeeded(countrySelect) {
    return !countrySelect.value || countrySelect.value === "us"
  }

  _markCreateState() {
    if (this.hasCreateStateMarkTarget) this.createStateMarkTarget.hidden = !this._stateNeeded(this.createCountryTarget)
  }

  editCountryChanged() {
    this._loadSubregions(this.editCountryTarget.value, this.editStateTarget)
  }

  async _loadSubregions(countryCode, stateSelect) {
    stateSelect.innerHTML = '<option value=""></option>'
    if (!countryCode) return

    try {
      const response = await fetch(`/locations/subregions?country_code=${countryCode}`, {
        headers: { Accept: "application/json" }
      })
      const subregions = await response.json()
      subregions.forEach(sr => {
        const opt = document.createElement("option")
        opt.value = sr.name
        opt.textContent = sr.name
        stateSelect.appendChild(opt)
      })
    } catch { /* ignore */ }
  }

  _selectedText(select) {
    return select.selectedIndex >= 0 ? select.options[select.selectedIndex].text : ""
  }

  // ── Create ────────────────────────────────────────────────────────

  closeCreateModal() {
    this._clearCreateErrors()
    super.closeCreateModal()
    this._markCreateState()
  }

  async submitCreate() {
    const body = {
      tag_name: {
        code: this.createCodeTarget.value.trim(),
        name: this.createNameTarget.value.trim(),
        address: this.createAddressTarget.value.trim(),
        city: this.createCityTarget.value.trim(),
        state: this.createStateTarget.value,
        zipcode: this.createZipcodeTarget.value.trim(),
        country: this._selectedText(this.createCountryTarget),
        region_id: this.createRegionTarget.value || null,
        affiliate_id: this.createAffiliateTarget.value || null,
        building_type_id: this.createBuildingTypeTarget.value || null
      }
    }
    if (!this._validateCreate(body.tag_name)) return

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeCreateModal()
        this._showToast(data.message || "Created")
        this._resetAndLoad()
      } else {
        this._showToast(data.errors?.join(", ") || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  _validateCreate(data) {
    this._clearCreateErrors()
    let valid = true

    const checks = [
      { value: data.code, input: this.createCodeTarget, error: this.createCodeErrorTarget },
      { value: data.name, input: this.createNameTarget, error: this.createNameErrorTarget },
      { value: data.address, input: this.createAddressTarget, error: this.createAddressErrorTarget },
      { value: data.city, input: this.createCityTarget, error: this.createCityErrorTarget },
      { value: data.country, input: this.createCountryTarget, error: this.createCountryErrorTarget }
    ]
    if (this._stateNeeded(this.createCountryTarget)) {
      checks.push({ value: data.state, input: this.createStateTarget, error: this.createStateErrorTarget })
    }

    checks.forEach(({ value, input, error }) => {
      if (!value || !value.trim()) {
        input.classList.add("border-red-400", "ring-1", "ring-red-400")
        if (error) error.classList.remove("hidden")
        valid = false
      }
    })

    return valid
  }

  _clearCreateErrors() {
    const inputs = [this.createCodeTarget, this.createNameTarget, this.createAddressTarget,
      this.createCityTarget, this.createCountryTarget, this.createStateTarget]
    inputs.forEach(el => el.classList.remove("border-red-400", "ring-1", "ring-red-400"))

    const errors = [this.createCodeErrorTarget, this.createNameErrorTarget,
      this.createAddressErrorTarget, this.createCityErrorTarget,
      this.createCountryErrorTarget, this.createStateErrorTarget]
    errors.forEach(el => el.classList.add("hidden"))
  }

  // ── Row Menu ────────────────────────────────────────────────────

  toggleRowMenu(event) {
    event.stopPropagation()
    const btn = event.currentTarget
    const menu = btn.nextElementSibling

    // Close any other open menus
    this.rowMenuTargets.forEach(m => { if (m !== menu) m.classList.add("hidden") })

    menu.classList.toggle("hidden")
    this._activeMenuTag = btn.dataset.tag

    // Close on outside click
    if (!menu.classList.contains("hidden")) {
      const close = (e) => { if (!menu.contains(e.target) && e.target !== btn) { menu.classList.add("hidden"); document.removeEventListener("click", close) } }
      document.addEventListener("click", close)
    }
  }

  editFromMenu(event) {
    event.stopPropagation()
    const tn = JSON.parse(this._activeMenuTag)
    this._openEditModal(tn)
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))
  }

  deleteFromMenu(event) {
    event.stopPropagation()
    const tn = JSON.parse(this._activeMenuTag)
    this.rowMenuTargets.forEach(m => m.classList.add("hidden"))
    this._confirmDelete(tn)
  }

  // ── Edit ──────────────────────────────────────────────────────────

  openEditModalFromData(event) {
    const tn = JSON.parse(event.currentTarget.dataset.tag)
    this._openEditModal(tn)
  }

  async _openEditModal(tn) {
    this.editIdTarget.value = tn.id
    this.editCodeTarget.value = tn.code || ""
    this.editNameTarget.value = tn.name || ""
    this.editAddressTarget.value = tn.address || ""
    this.editCityTarget.value = tn.city || ""
    if (this.hasEditZipcodeTarget) this.editZipcodeTarget.value = tn.zipcode || ""
    if (this.hasEditRegionTarget) this.editRegionTarget.value = tn.region_id || ""
    if (this.hasEditAffiliateTarget) this.editAffiliateTarget.value = tn.affiliate_id || ""
    if (this.hasEditBuildingTypeTarget) this.editBuildingTypeTarget.value = tn.building_type_id || ""

    // Set country and load subregions, then set state. A country the list
    // spells differently (an older import's "Cape Verde") stays as it is
    // instead of being wiped on save.
    const countryName = (tn.country || "").trim()
    this.editCountryTarget.querySelectorAll("option[data-kept]").forEach(o => o.remove())
    let countryOption = Array.from(this.editCountryTarget.options)
      .find(o => o.text.toLowerCase() === countryName.toLowerCase())
    if (!countryOption && countryName) {
      countryOption = new Option(countryName, "")
      countryOption.dataset.kept = "true"
      this.editCountryTarget.appendChild(countryOption)
    }
    this.editCountryTarget.selectedIndex = countryOption ? countryOption.index : 0

    // Clears the last property's states too when this one's country has none.
    await this._loadSubregions(this.editCountryTarget.value, this.editStateTarget)
    // Set state after subregions are loaded
    const stateOption = Array.from(this.editStateTarget.options).find(o => o.value === (tn.state || ""))
    if (stateOption) {
      this.editStateTarget.value = stateOption.value
    } else if (tn.state) {
      // State not in subregions list — add it as a custom option
      const opt = document.createElement("option")
      opt.value = tn.state
      opt.textContent = tn.state
      opt.selected = true
      this.editStateTarget.appendChild(opt)
    }

    this.editModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  _confirmDelete(tn) {
    this._pendingDeleteTag = tn
    if (this.hasConfirmNameTarget) this.confirmNameTarget.textContent = tn.code_name || tn.name
    this.confirmModalTarget.classList.remove("hidden")
  }

  cancelDelete() {
    this._pendingDeleteTag = null
    this.confirmModalTarget.classList.add("hidden")
  }

  async confirmDelete() {
    const tn = this._pendingDeleteTag
    if (!tn) return

    this.confirmModalTarget.classList.add("hidden")
    this._pendingDeleteTag = null

    try {
      const response = await fetch(`${this.baseUrlValue}/${tn.id}`, {
        method: "DELETE",
        headers: this._headers()
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this._showToast(data.message || "Deleted")
        this._resetAndLoad()
      } else {
        this._showToast(data.message || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  async submitUpdate() {
    const id = this.editIdTarget.value
    const body = {
      tag_name: {
        code: this.editCodeTarget.value.trim(),
        name: this.editNameTarget.value.trim(),
        address: this.editAddressTarget.value.trim(),
        city: this.editCityTarget.value.trim(),
        state: this.editStateTarget.value,
        zipcode: this.hasEditZipcodeTarget ? this.editZipcodeTarget.value.trim() : null,
        country: this._selectedText(this.editCountryTarget),
        region_id: this.hasEditRegionTarget ? this.editRegionTarget.value || null : null,
        affiliate_id: this.hasEditAffiliateTarget ? this.editAffiliateTarget.value || null : null,
        building_type_id: this.hasEditBuildingTypeTarget ? this.editBuildingTypeTarget.value || null : null
      }
    }

    try {
      const response = await fetch(`${this.baseUrlValue}/${id}`, {
        method: "PATCH",
        headers: this._headers(),
        body: JSON.stringify(body)
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok) {
        this.closeEditModal()
        this._showToast(data.message || "Updated")
        this._resetAndLoad()
      } else {
        this._showToast(data.errors?.join(", ") || "Error", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    }
  }

  // ── Import ────────────────────────────────────────────────────────

  openImportModal() {
    if (this.hasImportFileTarget) this.importFileTarget.value = ""
    if (this.hasImportButtonTarget) this.importButtonTarget.disabled = true
    this.importModalTarget.classList.remove("hidden")
    this._lockScroll()
  }

  closeImportModal() {
    this.importModalTarget.classList.add("hidden")
    this._unlockScroll()
  }

  onImportFileChange() {
    if (this.hasImportButtonTarget) {
      this.importButtonTarget.disabled = !this.importFileTarget.files.length
    }
  }

  async submitImport() {
    const file = this.importFileTarget.files[0]
    if (!file) return

    const btn = this.importButtonTarget
    btn._originalHTML = btn.innerHTML
    btn.disabled = true
    btn.innerHTML = `<svg class="animate-spin w-4 h-4 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`

    const formData = new FormData()
    formData.append("file", file)

    try {
      const response = await fetch(this.importUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: formData
      })
      if (response.ok) {
        this.closeImportModal()
        this._showToast("Import completed")
        this._resetAndLoad()
      } else {
        this._showToast("Import failed", "error")
      }
    } catch {
      this._showToast("Network error", "error")
    } finally {
      if (btn._originalHTML) {
        btn.innerHTML = btn._originalHTML
        delete btn._originalHTML
      }
      btn.disabled = false
    }
  }

  // ── Export ──────────────────────────────────────────────────────────

  async exportCsv(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.url
    const filename = event.currentTarget.dataset.filename

    // Close dropdown
    const dropdown = this.element.querySelector('[data-controller="dropdown"] [data-dropdown-target="menu"]')
    if (dropdown) dropdown.classList.add("hidden")

    this._showExportSpinner()

    try {
      const response = await fetch(url, { headers: { Accept: "text/csv" } })
      if (!response.ok) throw new Error("Export failed")

      const blob = await response.blob()
      const a = document.createElement("a")
      a.href = URL.createObjectURL(blob)
      const today = new Date().toISOString().split("T")[0]
      a.download = `${filename}-${today}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(a.href)
      this._showToast("Export downloaded")
    } catch {
      this._showToast("Export failed", "error")
    } finally {
      this._hideExportSpinner()
    }
  }

  _showExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    btn._originalHTML = btn.innerHTML
    btn.style.width = `${btn.offsetWidth}px`
    btn.classList.add("justify-center")
    btn.disabled = true
    btn.innerHTML = `<svg class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
  }

  _hideExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    if (btn._originalHTML) {
      btn.innerHTML = btn._originalHTML
      delete btn._originalHTML
    }
    btn.style.width = ""
    btn.classList.remove("justify-center")
    btn.disabled = false
  }

  async toggleAllowCustomProjectName() {
    const checkbox = this.allowCustomProjectNameTarget
    const newStatus = checkbox.checked

    try {
      const resp = await fetch(this.toggleCustomNameUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ status: newStatus })
      })
      if (resp.ok) {
        this._showToast("Setting updated")
      } else {
        checkbox.checked = !newStatus
        this._showToast("Error updating setting", "error")
      }
    } catch {
      checkbox.checked = !newStatus
      this._showToast("Network error", "error")
    }
  }
}
